import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { AppSkinProps } from '../../../types/decisions';
import { getFieldName } from '../../../utils';
import { TriangleSlider, Slider, HeartSlider } from '../../slider';
import CriteriaSettingV2 from './CriteriaSettingV2';
import Modal from '../../modal/Modal';
import { useAppConfig } from '../../../context/app-configs-context';
import NameWithIcon from './NameWithIcon/NameWithIcon';
import GuidedTour from '../../GuidedTour/GuidedTour';
import { trackActivity } from '../../../api/utils.api';
import { AUTO_SAVE_DELAY, CriteriaImportanceAndSettingsProps, steps } from './CriteriaImportanceAndSettings';
import './CriteriaImportanceAndSettingsV2.scss';

const AGENT_FOCUS_CLOSE_DELAY = 2000;

type CustomProps = {
    highlightedFieldIds?: Set<string>;
    highlightedOptions?: Record<string, string[]>;
    revealedOptionsByField?: Record<string, string[]>;
    changedOptionsByField?: Record<string, Record<string, { from: number; to: number }>>;
    agentFocusedFieldId?: string | null;
    modalClassName?: string;
    stackAgentPopups?: boolean;
    shouldUseHeartSlider?: boolean;
}

const getSliderLabel = (value: number): string => {
    if (value < 40) {
        return 'Not important';
    }
    if (value < 60) {
        return 'Important';
    }
    if (value < 80) {
        return 'Very important';
    }

    return 'Most important';
};

const CriteriaImportanceAndSettingsV2 = ({
    criteriaFields,
    onSliderChange,
    fieldInitialValues,
    onTradeoffValueChange,
    // onResetTradeOffs,
    onCriteriaApply,
    renderCustomSettings,
    externalItems = [],
    decisionConfig,
    appId,
    shouldUseRegularSlider = false,
    highlightedFieldIds,
    highlightedOptions,
    revealedOptionsByField,
    changedOptionsByField,
    agentFocusedFieldId,
    modalClassName,
    stackAgentPopups = false,
    shouldUseHeartSlider = false,
}: CriteriaImportanceAndSettingsProps & CustomProps): React.JSX.Element => {
    const location = useLocation();
    const isDisabled = location.pathname.startsWith('/patient-choice');
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';

    const { appConfigs: { criteriaSettings: { popup } } } = useAppConfig();

    const [ openFieldId, setOpenFieldId ] = useState<string | null>(null);
    const [ agentPopupStack, setAgentPopupStack ] = useState<string[]>([]);
    const [ revealedHeartFieldIds, setRevealedHeartFieldIds ] = useState<Set<string>>(new Set());

    const criteriaChangedOnceRef = useRef<{ [key: string]: boolean; }>({});
    const agentFocusCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onCriteriaApplyRef = useRef(onCriteriaApply);
    const datasetRef = useRef(dataset);

    const shouldRankOnDecideButtonClick = decisionConfig?.shouldRankOnDecideButtonClick ?? true;

    useEffect(() => {
        onCriteriaApplyRef.current = onCriteriaApply;
    }, [ onCriteriaApply ]);

    useEffect(() => {
        datasetRef.current = dataset;
    }, [ dataset ]);

    const clearAutoSaveTimeout = () => {
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
            autoSaveTimeoutRef.current = null;
        }
    };

    const scheduleAutoCriteriaApply = () => {
        if (shouldRankOnDecideButtonClick || isDisabled) {
            return;
        }

        clearAutoSaveTimeout();

        autoSaveTimeoutRef.current = setTimeout(() => {
            onCriteriaApplyRef.current(datasetRef.current);
            autoSaveTimeoutRef.current = null;
        }, AUTO_SAVE_DELAY);
    };

    useEffect(() => {
        return () => {
            clearAutoSaveTimeout();
        };
    }, []);

    useEffect(() => {
        if (!agentFocusedFieldId) {
            if (!stackAgentPopups) {
                if (agentFocusCloseTimeoutRef.current) {
                    clearTimeout(agentFocusCloseTimeoutRef.current);
                }

                agentFocusCloseTimeoutRef.current = setTimeout(() => {
                    handleClose();
                    agentFocusCloseTimeoutRef.current = null;
                }, AGENT_FOCUS_CLOSE_DELAY);
            }

            return;
        }

        if (agentFocusCloseTimeoutRef.current) {
            clearTimeout(agentFocusCloseTimeoutRef.current);
            agentFocusCloseTimeoutRef.current = null;
        }

        if (stackAgentPopups) {
            const field = criteriaFields.find(item => item.id === agentFocusedFieldId);

            if (!field || field.hide_field) {
                return;
            }

            setAgentPopupStack(prev => (prev.includes(agentFocusedFieldId) ? prev : [ ...prev, agentFocusedFieldId ]));

            return;
        }

        openCriteriaSettings(agentFocusedFieldId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ agentFocusedFieldId, stackAgentPopups ]);

    useEffect(() => {
        return () => {
            if (agentFocusCloseTimeoutRef.current) {
                clearTimeout(agentFocusCloseTimeoutRef.current);
            }
        };
    }, []);

    const handleSliderChange: AppSkinProps['onSliderChange'] = (...args) => {
        onSliderChange(...args);
        scheduleAutoCriteriaApply();
    };

    const handleTradeoffValueChange: AppSkinProps['onTradeoffValueChange'] = (...args) => {
        onTradeoffValueChange(...args);
        scheduleAutoCriteriaApply();
    };

    // const handleResetTradeOffs = () => {
    //     onResetTradeOffs();
    //     scheduleAutoCriteriaApply();
    // };

    const handleExternalLinkClick = (label: string, link: string) => {
        if (appId) {
            trackActivity({
                app_id: appId,
                event: 'External Link',
                action: label,
                field: label,
                row_id: appId,
                referenced_row_id: appId,
                url: link,
                link: link,
            }).catch(console.error);
            console.log(appId, label, link);
        }

        window.open(link, '_blank', 'noopener,noreferrer');
    };

    const handleClose = () => {
        setOpenFieldId(null);
    };

    const openCriteriaSettings = (id: string) => {
        const field = criteriaFields.find(item => item.id === id);

        if (!field || field.hide_field) {
            return;
        }

        setOpenFieldId(id);
    };

    const openField = openFieldId ? criteriaFields.find(item => item.id === openFieldId) : null;
    const isOpenFieldAgentDriven = !!openFieldId && openFieldId === agentFocusedFieldId;

    const closeAgentPopup = (id: string) => {
        setAgentPopupStack(prev => prev.filter(fieldId => fieldId !== id));
    };

    const onAutoPopupCheck = (newImportance: number, id: string) => {
        if (!popup.show || newImportance < popup.threshold) {
            return;
        }

        if (!criteriaChangedOnceRef.current[id]) {
            criteriaChangedOnceRef.current[id] = true;
            openCriteriaSettings(id);
        }
    };

    const handleImportanceChangeCommitted = (newImportance: number, id: string) => {
        onAutoPopupCheck(newImportance, id);
        scheduleAutoCriteriaApply();
    };

    const revealHeartField = () => {
        setRevealedHeartFieldIds(new Set(criteriaFields.map(field => field.id)));
    };

    let SliderComponent = TriangleSlider;
    if (shouldUseRegularSlider) {
        SliderComponent = Slider;
    }

    const isAnyHeartFieldRevealed = revealedHeartFieldIds.size > 0 || !!highlightedFieldIds?.size;

    return (
        <div className="app-criteria-and-settings-v2">
            {shouldUseHeartSlider && (
                isAnyHeartFieldRevealed ? (
                    <div className="app-criteria-importance-legend">
                        <span>Not important</span>
                        <span className="app-criteria-importance-legend-bar"/>
                        <span className="is-high">Very important</span>
                    </div>
                ) : (
                    <div className="app-criteria-importance-hint">
                        What&apos;s important?
                    </div>
                )
            )}
            <div className="app-criteria-fields" id="app-criteria-fields">
                {criteriaFields.map(({ id, name, hide_importance, hide_field, icon, link }, idx) => {
                    const criteriaName = getFieldName(name);
                    const isMentioned = !!highlightedFieldIds?.has(id);

                    if (shouldUseHeartSlider) {
                        const revealed = isMentioned || revealedHeartFieldIds.has(id);

                        return (
                            <div
                                key={id}
                                className={`app-criteria-field ${hide_importance ? 'hidden' : ''} ${isMentioned ? 'criteria-highlighted' : ''} heart-slider-field`}
                            >
                                <HeartSlider
                                    name={criteriaName}
                                    link={link}
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={+criteriaFields[idx].importance}
                                    onChange={(_, value) => handleSliderChange(value as number, id)}
                                    onChangeCommitted={(_, value) => handleImportanceChangeCommitted(value as number, id)}
                                    disabled={isDisabled || hide_field}
                                    highlighted={isMentioned}
                                    revealed={revealed}
                                    onNameClick={revealHeartField}
                                    onSettingsClick={() => openCriteriaSettings(id)}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={getSliderLabel}
                                />
                            </div>
                        );
                    }

                    return (
                        <div
                            key={id}
                            className={`app-criteria-field ${hide_importance ? 'hidden' : ''} ${isMentioned ? 'criteria-highlighted' : ''}`}
                        >
                            <label>
                                <NameWithIcon icon={icon} name={criteriaName} link={link}/>

                                <SliderComponent
                                    name={id}
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={+criteriaFields[idx].importance}
                                    onChange={(_, value) => handleSliderChange(value as number, id)}
                                    onChangeCommitted={(_, value) => handleImportanceChangeCommitted(value as number, id)}
                                    isEditingDisabled={hide_field}
                                    onEditIconClick={() => openCriteriaSettings(id)}
                                    disabled={isDisabled}
                                />
                            </label>
                        </div>
                    );
                })}
            </div>
            <div className="app-criteria-actions" style={{ display: 'block' }}>
                {externalItems?.length > 0 && (
                    <div className="additional-menu-items" style={{ marginTop: 32 }}>
                        <h3>External links</h3>

                        <div
                            className="additional-menu-items-content"
                            style={{
                                marginTop: 16,
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                            }}
                        >
                            {externalItems.map(item => (
                                <Button
                                    key={`${item.label}-${item.link}`}
                                    className="app-criteria-actions-action"
                                    type="button"
                                    variant="outlined"
                                    sx={{
                                        textTransform: 'none',
                                        width: '48%',
                                        mb: 1,
                                    }}
                                    onClick={() => handleExternalLinkClick(item.label, item.link)}
                                >
                                    {item.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <Modal isOpen={!!openField} handleClose={handleClose} className={modalClassName}>
                {!!openField ? (
                    <CriteriaSettingV2
                        field={openField}
                        fieldInitialValues={fieldInitialValues}
                        onTradeoffValueChange={handleTradeoffValueChange}
                        onSliderChange={handleSliderChange}
                        handleModalClose={handleClose}
                        areCTAsDisplayed
                        displayFullWidth
                        disabled={isDisabled}
                        renderCustomSettings={renderCustomSettings}
                        highlightedOptionNames={highlightedOptions?.[openField.id]}
                        {...(isOpenFieldAgentDriven ? {
                            revealedOptionNames: revealedOptionsByField?.[openField.id] ?? [],
                            autoExpandedOptionValues: changedOptionsByField?.[openField.id],
                        } : {})}
                    />
                ) : <></>}
            </Modal>

            {stackAgentPopups && agentPopupStack.map((fieldId, index) => {
                const field = criteriaFields.find(item => item.id === fieldId);

                if (!field) {
                    return null;
                }

                const isTopOfStack = index === agentPopupStack.length - 1;

                return (
                    <Modal
                        key={fieldId}
                        isOpen
                        handleClose={() => closeAgentPopup(fieldId)}
                        className={`agent-stack-popup agent-stack-pos-${index} ${isTopOfStack ? '' : 'agent-stack-backdrop-hidden'}`}
                    >
                        <CriteriaSettingV2
                            field={field}
                            fieldInitialValues={fieldInitialValues}
                            onTradeoffValueChange={handleTradeoffValueChange}
                            onSliderChange={handleSliderChange}
                            handleModalClose={() => closeAgentPopup(fieldId)}
                            areCTAsDisplayed
                            displayFullWidth
                            disabled={isDisabled}
                            renderCustomSettings={renderCustomSettings}
                            highlightedOptionNames={highlightedOptions?.[field.id]}
                            revealedOptionNames={revealedOptionsByField?.[field.id] ?? []}
                            autoExpandedOptionValues={changedOptionsByField?.[field.id]}
                        />
                    </Modal>
                );
            })}

            <GuidedTour
                storageKey="new-decision-settings-tour-v1"
                steps={steps}
            />
        </div>
    );
};

export default CriteriaImportanceAndSettingsV2;